import apiService, { ApiError } from './apiService';

class EarningsBonusesService {
  formatDateForAPI(date, isEndDate = false) {
    if (!date) return null;

    const dateObj = new Date(date);
    if (Number.isNaN(dateObj.getTime())) return null;

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const time = isEndDate ? '23:59:59' : '00:00:00';

    return `${year}-${month}-${day} ${time}`;
  }

  buildPayload(
    params = {},
    {
      defaultOrder,
      includePagination = true,
      includeChannel = false,
      includeType = true,
    } = {}
  ) {
    const {
      page = 1,
      size = 20,
      order,
      keyword,
      shoppingCycleCode,
      dateFrom,
      dateTo,
      channel,
      type,
    } = params;

    const payload = {
      order: order || defaultOrder,
    };

    if (includePagination) {
      payload.page = page;
      payload.size = size;
    }

    const trimmedKeyword = String(keyword || '').trim();
    if (trimmedKeyword.length >= 3) {
      payload.keyword = trimmedKeyword;
    }

    const startDate = this.formatDateForAPI(dateFrom, false);
    const endDate = this.formatDateForAPI(dateTo, true);

    if (startDate && endDate) {
      payload.dateFilter = {
        start: startDate,
        end: endDate,
      };
    } else if (shoppingCycleCode === 'all') {
      payload.dateFilter = {
        start: '1970-01-01 00:00:00',
        end: '2999-12-31 23:59:59',
      };
    } else if (shoppingCycleCode && shoppingCycleCode !== 'current') {
      payload.shoppingCycleCode = shoppingCycleCode;
    }

    if (includeChannel) {
      payload.channel = channel || '';
    }

    if (includeType) {
      payload.type = type || '';
    }

    return payload;
  }

  ensureId(id, label) {
    if (!id) {
      throw new ApiError(`${label} is required`, 400, 'MISSING_ID');
    }
  }

  handleError(error, message, code, data = {}) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(message, 0, code, {
      ...data,
      originalError: error.message,
    });
  }

  async getShoppingCycles(limit = 3, options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/app/shopping-cycles-recent',
        {
          method: 'GET',
          params: { limit },
          ...options,
        }
      );

      if (!response || !Array.isArray(response.data)) {
        throw new ApiError(
          'Invalid response format from shopping cycles API',
          200,
          'INVALID_RESPONSE',
          response
        );
      }

      return response;
    } catch (error) {
      this.handleError(
        error,
        'Failed to retrieve shopping cycles',
        'SHOPPING_CYCLES_ERROR'
      );
    }
  }

  async getCreatorMarketingBoost(creatorId, params = {}, options = {}) {
    this.ensureId(creatorId, 'Creator ID');

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${creatorId}/earnings/marketing-boost`,
        {
          method: 'POST',
          data: this.buildPayload(params, {
            defaultOrder: 'b.created_at DESC',
            includeChannel: true,
            includeType: true,
          }),
          ...options,
        }
      );

      return response;
    } catch (error) {
      this.handleError(
        error,
        'Failed to retrieve marketing boost earnings',
        'MARKETING_BOOST_ERROR',
        { creatorId }
      );
    }
  }

  async exportCreatorMarketingBoost(creatorId, params = {}, options = {}) {
    this.ensureId(creatorId, 'Creator ID');

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${creatorId}/earnings/marketing-boost/export`,
        {
          method: 'POST',
          data: this.buildPayload(params, {
            defaultOrder: 'b.created_at DESC',
            includePagination: false,
            includeChannel: true,
            includeType: true,
          }),
          ...options,
        }
      );

      return response;
    } catch (error) {
      this.handleError(
        error,
        'Failed to export marketing boost earnings',
        'MARKETING_BOOST_EXPORT_ERROR',
        { creatorId }
      );
    }
  }

  async getCreatorVolumePoint(creatorId, params = {}, options = {}) {
    this.ensureId(creatorId, 'Creator ID');

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${creatorId}/earnings/volume-point`,
        {
          method: 'POST',
          data: this.buildPayload(params, {
            defaultOrder: 'cp.created_at DESC',
            includeType: true,
          }),
          ...options,
        }
      );

      return response;
    } catch (error) {
      this.handleError(
        error,
        'Failed to retrieve volume point earnings',
        'VOLUME_POINT_ERROR',
        { creatorId }
      );
    }
  }

  async exportCreatorVolumePoint(creatorId, params = {}, options = {}) {
    this.ensureId(creatorId, 'Creator ID');

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${creatorId}/earnings/volume-point/export`,
        {
          method: 'POST',
          data: this.buildPayload(params, {
            defaultOrder: 'cp.created_at DESC',
            includePagination: false,
            includeType: true,
          }),
          ...options,
        }
      );

      return response;
    } catch (error) {
      this.handleError(
        error,
        'Failed to export volume point earnings',
        'VOLUME_POINT_EXPORT_ERROR',
        { creatorId }
      );
    }
  }

  async getShopperEarnings(shopperId, params = {}, options = {}) {
    this.ensureId(shopperId, 'Shopper ID');

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/shopper/admin/${shopperId}/earnings`,
        {
          method: 'POST',
          data: this.buildPayload(params, {
            defaultOrder: 'sb.created_at DESC',
            includeChannel: true,
            includeType: true,
          }),
          ...options,
        }
      );

      return response;
    } catch (error) {
      this.handleError(
        error,
        'Failed to retrieve shopper earnings',
        'SHOPPER_EARNINGS_ERROR',
        { shopperId }
      );
    }
  }

  async exportShopperEarnings(shopperId, params = {}, options = {}) {
    this.ensureId(shopperId, 'Shopper ID');

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/shopper/admin/${shopperId}/earnings/export`,
        {
          method: 'POST',
          data: this.buildPayload(params, {
            defaultOrder: 'sb.created_at DESC',
            includePagination: false,
            includeChannel: true,
            includeType: true,
          }),
          ...options,
        }
      );

      return response;
    } catch (error) {
      this.handleError(
        error,
        'Failed to export shopper earnings',
        'SHOPPER_EARNINGS_EXPORT_ERROR',
        { shopperId }
      );
    }
  }
}

const earningsBonusesService = new EarningsBonusesService();

export default earningsBonusesService;
export { EarningsBonusesService };
